import { useEffect, useMemo, useRef } from 'react';
import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../../store/store';
import StaticPageLayout from '../../../components/layouts/StaticPageLayout';
import useHistory from '../../../hooks/useHistory';
import { encodeProjectsParams } from '../../Projects/constants';
import { decodeSharedProjectsParams, SHARED_PROJECT_TYPE_TABS } from './constants';
import { paths } from '../../../utils/constants/routes';
import GeneralLoader from '../../../components/loaders/GeneralLoader';
import GeneralError from '../../../components/errors/GeneralError';
import ProfilerSharedProjectsList from './ProfilerSharedProjectsList';
import ProfilerFeedbackRequestView from './ProfilerFeedbackRequestView';
import pubSub from '../../../utils/pubsub';
import { runInAction } from 'mobx';
import useElementHeightCssVar from '../../../hooks/useElementHeightCssvar';
import { eventTeamPageViewed, eventTeamTabChanged } from '../../../ga4/ga4';

const ProfilerTeam = observer(() => {
  const { location, navigate } = useHistory();
  const { profilerProjectStore, authStore, utilsStore } = useStore();
  const tabsRef = useRef(null);
  const state = useLocalObservable(() => ({
    isRendered: true,
    unrender: () => (state.isRendered = false),
    params: {},
    setParams: (params = {}) => {
      state.params = params;
    },
    get layout() {
      return utilsStore.windowWidth < 1201 ? 'mobile' : 'desktop';
    },
    savedProjectListScrollY: 0,
    setSavedProjectListScrollY: (height = 0) => {
      runInAction(() => {
        state.savedProjectListScrollY = height;
      });
    }
  }));

  useElementHeightCssVar('#root .tabs-wrap', '--team-feedback-projects-list-tabs-height');
  useElementHeightCssVar('#root .tabs.request-view', '--team-feedback-project-view-tabs-height');
  // The sticky toolbar row above the tabs; the tabs, the column header and the
  // heatmap height offset themselves by it (team-feedback.less). It replaced the in-grid
  // results header, whose height var had no other consumer and is no longer measured.
  useElementHeightCssVar(
    '.page-team-feedback .team-feedback-toolbar',
    '--team-feedback-toolbar-height'
  );
  // TD-2733 — the stacked heatmap's own chrome. Scoped to this page because the hook writes
  // to document.documentElement and the Projects and Search pages measure the same names.
  useElementHeightCssVar(
    '.page-team-feedback .profiler-heatmap-section-header',
    '--profiler-heatmap-section-header-height'
  );
  useElementHeightCssVar(
    '.page-team-feedback .profiler-heatmap-controls-header.mobile-view',
    '--profiler-heatmap-controls-mobile-height'
  );
  useElementHeightCssVar(
    '.page-team-feedback .profiler-heatmap-section .heatmap-legend',
    '--heatmap-legend-height'
  );

  useEffect(() => {
    return () => {
      state.unrender();
    };
  }, [state]);

  useEffect(() => {
    authStore.loadCompanyUsers();
  }, [authStore]);

  useEffect(() => {
    const savedProjectListHandler = (payload) => {
      if (payload.event === 'team-saved-project-list-scroll-y') {
        const _scrollY = window.scrollY;
        setTimeout(() => {
          state.setSavedProjectListScrollY(_scrollY);
        }, 100);
      }
    };

    pubSub.subscribe(savedProjectListHandler);

    return () => {
      pubSub.unsubscribe(savedProjectListHandler);
    };
  }, [state]);

  useEffect(() => {
    if (profilerProjectStore.isLoadingProjectsData) {
      return;
    }
    let [urlParams, params, shouldNavigate] = [null, {}, false];
    try {
      urlParams = new URLSearchParams(location.search);
      params = decodeSharedProjectsParams(urlParams.get('params'));
    } catch {
      params = { projectTypeTab: SHARED_PROJECT_TYPE_TABS.PENDING };
      shouldNavigate = true;
    }
    if (typeof params !== 'object' || params === null || params === undefined) {
      params = {};
    }

    if (!Object.values(SHARED_PROJECT_TYPE_TABS).includes(params.projectTypeTab)) {
      params.projectTypeTab = SHARED_PROJECT_TYPE_TABS.PENDING;
      shouldNavigate = true;
    }

    if (shouldNavigate) {
      navigate(paths.PROFILER_TEAM + '?params=' + encodeProjectsParams(params), {
        replace: true
      });
    }

    state.setParams(params);
  }, [
    state,
    profilerProjectStore,
    profilerProjectStore.allProjects,
    profilerProjectStore.isLoadingProjectsData,
    location.search
  ]);

  const teamAnalyticsRef = useRef({ viewed: false, tab: null });
  useEffect(() => {
    const a = teamAnalyticsRef.current;
    if (!a.viewed && !profilerProjectStore.isLoadingProjectsData) {
      a.viewed = true;
      eventTeamPageViewed(
        profilerProjectStore.pendingSharedProjects.length,
        profilerProjectStore.resolvedSharedProjects.length
      );
    }
    const tab = state.params.projectTypeTab;
    if (a.tab && tab && tab !== a.tab) {
      eventTeamTabChanged(tab);
    }
    a.tab = tab;
  }, [
    profilerProjectStore.isLoadingProjectsData,
    profilerProjectStore.pendingSharedProjects.length,
    profilerProjectStore.resolvedSharedProjects.length,
    state.params.projectTypeTab
  ]);

  const projectList = useMemo(() => {
    if (state.params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.PENDING) {
      return profilerProjectStore.pendingSharedProjects
        .slice()
        .sort(
          (a, b) =>
            b.sharedUsers.find((u) => u.id === authStore.currentUserId).dateReceived -
            a.sharedUsers.find((u) => u.id === authStore.currentUserId).dateReceived
        );
    } else {
      return profilerProjectStore.resolvedSharedProjects
        .slice()
        .sort(
          (a, b) =>
            b.sharedUsers.find((u) => u.id === authStore.currentUserId).dateResolved -
            a.sharedUsers.find((u) => u.id === authStore.currentUserId).dateResolved
        );
    }
  }, [
    profilerProjectStore.projects,
    state.params,
    profilerProjectStore.pendingSharedProjects.length,
    profilerProjectStore.resolvedSharedProjects.length
  ]);

  const selectedProject = useMemo(() => {
    if (state.params.selectedProjectId) {
      const projectList =
        state.params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.PENDING
          ? profilerProjectStore.pendingSharedProjects
          : profilerProjectStore.resolvedSharedProjects;
      return projectList.find((project) => project.id === state.params.selectedProjectId);
    }
    return null;
  }, [profilerProjectStore.projects, state.params]);

  const showError =
    profilerProjectStore.loadProjectsError && !profilerProjectStore.allProjects.length;

  const showProjectList =
    !showError &&
    Object.values(SHARED_PROJECT_TYPE_TABS).includes(state.params.projectTypeTab) &&
    !state.params.selectedProjectId;
  const showFeedbackRequest = !showError && state.params.selectedProjectId && selectedProject;

  const scrollMobileTabIntoView = (tabId = '') => {
    const tabDOM = document.getElementById(tabId);
    if (tabDOM) {
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setTimeout(() => {
          tabDOM.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }, 500);
      }, 100);
    }
  };

  const tabs = [
    {
      id: 'team_mobile_open_requests_tab',
      name: `Open requests (${profilerProjectStore.pendingSharedProjects.length})`,
      onClick: () => {
        if (state.params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.PENDING) {
          return;
        }
        navigate(
          paths.PROFILER_TEAM +
            '?params=' +
            encodeProjectsParams({ projectTypeTab: SHARED_PROJECT_TYPE_TABS.PENDING })
        );
        scrollMobileTabIntoView('team_mobile_open_requests_tab');
      },
      isActive: state.params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.PENDING
    },
    {
      id: 'team_mobile_resolved_requests_tab',
      name: `Resolved requests (${profilerProjectStore.resolvedSharedProjects.length})`,
      onClick: () => {
        if (state.params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.RESOLVED) {
          return;
        }
        navigate(
          paths.PROFILER_TEAM +
            '?params=' +
            encodeProjectsParams({ projectTypeTab: SHARED_PROJECT_TYPE_TABS.RESOLVED })
        );
        scrollMobileTabIntoView('team_mobile_resolved_requests_tab');
      },
      isActive: state.params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.RESOLVED
    }
  ];

  if (showError) {
    return (
      <StaticPageLayout page="team-feedback">
        {showError && (
          <GeneralError
            errorMessage={profilerProjectStore.loadProjectsError}
            actionMessage="You may want to:"
            withHomePageButton={true}
            actionButtontext="Try again"
            onActionButtonClick={profilerProjectStore.loadProjects}
          />
        )}
      </StaticPageLayout>
    );
  }
  if (
    profilerProjectStore.initialLoadProjects ||
    (profilerProjectStore.isLoadingProjects && !profilerProjectStore.allProjects.length)
  ) {
    return (
      <StaticPageLayout page="team-feedback">
        <GeneralLoader />
      </StaticPageLayout>
    );
  }

  if (showProjectList && state.savedProjectListScrollY !== 0) {
    const top = state.savedProjectListScrollY;
    state.setSavedProjectListScrollY(0);
    setTimeout(() => {
      window.scrollTo({ top });
    }, 0);
  }

  return (
    <StaticPageLayout page="team-feedback">
      {showProjectList && (
        <>
          <div className="tabs-wrap" ref={tabsRef}>
            <div className="tabs at-projects-listing">
              {tabs.map((tab) => {
                return (
                  <div
                    key={tab.name}
                    id={tab.id}
                    onClick={tab.onClick}
                    className={`tab ${tab.isActive && 'active'} noselect`}
                  >
                    {tab.name}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="table-responsive">
            {!!projectList.length && (
              <div className="projects-list-header">
                <div className="table-head">
                  <div className="item">Sender name</div>
                  <div className="item">Project name</div>
                  <div className="item">Profile</div>
                  <div className="item">Industries</div>
                  <div className="item">Results</div>
                  <div className="item">
                    {state.params.projectTypeTab === SHARED_PROJECT_TYPE_TABS.PENDING
                      ? 'Date received'
                      : 'Date resolved'}
                  </div>
                </div>
              </div>
            )}
            <ProfilerSharedProjectsList projectList={projectList} params={state.params} />
          </div>
        </>
      )}
      {showFeedbackRequest && (
        <ProfilerFeedbackRequestView project={selectedProject} params={state.params} />
      )}
    </StaticPageLayout>
  );
});

export default ProfilerTeam;
